<template>
    <main>
        <Head>
            <title>{{ product.meta_title }}</title>
            <meta name="description" :content="product.meta_description" inertia="description" />
            <link rel="canonical" :href="url('product', product.slug)" />
            <link v-if="mainImage" rel="preload" :href="mainImage" as="image" type="image/jpeg" fetchpriority="low" />
            <link
                v-if="mainImageWebp"
                rel="preload"
                :href="mainImageWebp"
                as="image"
                type="image/webp"
                fetchpriority="high"
            />
            <link v-if="logo" rel="preload" :href="logo" as="image" fetchpriority="high" />
        </Head>

        <div class="bg-white/30 w-full pb-8">
            <!-- Admin edit links -->
            <div
                class="flex justify-end relative max-w-(--breakpoint-2xl) mx-auto space-x-2"
                v-if="user && user.is_admin && [30, 25, 33595].includes(user.id)"
            >
                <div class="flex items-center space-x-2 absolute right-8 top-3">
                    <a
                        target="_blank"
                        :href="`https://nv.doorax.nl/nova-beheer/resources/products/${product.id}/edit`"
                        class="text-xs text-gray-500 hover:text-gray-700 border border-orange-500 rounded-md px-2 py-1"
                    >
                        <fa :icon="['fal', 'pencil']" class="h-3 w-3" />
                        Nova
                    </a>
                    <a
                        target="_blank"
                        :href="`https://content.doorax.nl/product-find/${product.id}`"
                        class="text-xs text-gray-500 hover:text-gray-700 border border-blue-500 rounded-md px-2 py-1"
                    >
                        <fa :icon="['fal', 'pencil']" class="h-3 w-3" />
                        Content
                    </a>
                </div>
            </div>

            <!-- Message if $showResource is true -->
            <div
                v-if="showResource"
                class="bg-red-500/10 text-red-500 my-4 text-center py-2 rounded-md mx-auto max-w-2xl"
            >
                Product is uitgeschakeld.
            </div>

            <!-- <div class="w-full pb-8"> -->
            <div class="max-w-(--breakpoint-2xl) container mx-auto px-4 lg:px-4 pt-4 pb-8">
                <Breadcrumbs />

                <div class="flex flex-wrap">
                    <!-- Left -->
                    <div class="w-full md:w-1/2 md:pr-6 pb-6">
                        <div class="z-10 relative mb-4">
                            <!-- Title -->
                            <h1 class="text-lg font-bold mb-2">{{ product.name }}</h1>

                            <!-- Rating -->
                            <div class="flex items-center" v-if="product.reviews.count">
                                <review-stars :score="product.reviews.score" />
                                <span
                                    class="ml-2 text-sm text-gray-600 link"
                                    @click="$scrollTo('#product-reviews', 500)"
                                    >{{ product.reviews.score }}/5 ({{ product.reviews.count }}
                                    {{ $t('beoordelingen') }})</span
                                >
                            </div>
                        </div>

                        <!-- Images -->
                        <!-- Images -->
                        <ProductImages :images="images" :product="product" />
                    </div>

                    <!-- Right -->
                    <div class="w-full md:w-1/2 relative">
                        <!-- <div
                            v-if="data.loading"
                            class="z-50 inset-0 bg-white/30 absolute transition-all"
                        ></div> -->
                        <fa
                            :icon="['fad', 'spinner-third']"
                            class="text-dgreen opacity-50 fa-spin mx-auto w-8 h-8 absolute right-8 top-8"
                            v-if="data.loading"
                        />

                        <!-- Options -->
                        <!-- <div class="bg-white md:rounded-xl shadow-overallsm -mx-4 md:mx-0 transition-all" :class="{'blur-xs':data.loading}"> -->
                        <div class="bg-white md:rounded-xl shadow-overallsm -mx-4 md:mx-0 transition-all pt-4">
                            <div class="px-8 py-2">
                                <!-- Keys -->
                                <div
                                    class="my-3"
                                    v-if="[16, 84, 101, 102].includes(product.category_id) && product.relativeProduct"
                                >
                                    <strong class="my-1 min-w-0 flex font-semibold text-lg items-center"
                                        >Sleutels</strong
                                    >

                                    <div class="text-gray-600 my-2 mb-4">
                                        <div>
                                            Standaard
                                            <strong class="text-gray-800"
                                                >{{ product.reorder_type == 1 ? 0 : 3 }} sleutel(s)</strong
                                            >
                                            meegeleverd
                                        </div>
                                    </div>

                                    <!-- Keys -->
                                    <div class="flex flex-wrap">
                                        <extra-keys
                                            v-model:keyQty="form.keys.qty"
                                            v-model:keysSelected="form.keysSelected"
                                            v-model:caps="form.keys.colors"
                                            v-model:customColors="form.keys.customColors"
                                            :default_qty="product.reorder_type == 1 ? 0 : 3"
                                            :product="product"
                                            :compact="true"
                                        />
                                    </div>
                                </div>

                                <!-- Certificaat -->
                                <div class="my-4" v-if="product.reorder_type">
                                    <strong class="my-1 min-w-0 flex font-semibold text-lg items-center"
                                        >Upload certificaat</strong
                                    >
                                    <p class="text-gray-600 my-2 mb-4">
                                        Voor het nabestellen hebben wij een kopie of foto van uw cerfiticaat nodig
                                        waarbij de code zichtbaar is.
                                    </p>

                                    <upload-file v-model:file="form.file" v-if="product.reorder_type" />
                                    <Validation :error="form.errors.upload" />
                                </div>

                                <div class="config my-4" v-for="option in options" :key="option.id">
                                    <div v-if="option.values.length > 1">
                                        <div class="w-full mb-2 mt-3 lg:mt-0">
                                            <info-modal
                                                :title="option.title"
                                                :body="option.description"
                                                v-if="option.description"
                                            >
                                                <strong class="inline-block">{{ option.title }}</strong>
                                            </info-modal>
                                            <strong v-else class="inline-block">{{ option.title }}</strong>
                                        </div>

                                        <div class="flex flex-wrap -mx-2 mt-2" v-if="option.type == 'version'">
                                            <div
                                                class="rounded-md hover:bg-gray-50 border cursor-pointer mx-2 mb-2 relative items-center"
                                                v-for="value in option.values"
                                                :class="{
                                                    'bg-dgreen2/5 border-dgreen2':
                                                        value.id == data.values[option.id].id,
                                                }"
                                                :key="value.id"
                                                @click="value.url ? (data.loading = true) : false"
                                            >
                                                <Link :href="value.url" v-if="value.url" class="inset-0 absolute" />
                                                <span class="block p-2">
                                                    <!-- <div class="mb-2">
                                                    <span>{{ value.value }}</span>
                                                    <span v-if="value.product_price" class="font-bold ml-2">{{ price(value.product_price) }}</span>
                                                </div> -->
                                                    <vue-picture :image="value.image" :style="'h-16'" />
                                                    <!-- {{ price(value.price) }} -->
                                                </span>

                                                <!-- <fa
                                                :icon="['fal', 'check-circle']"
                                                class="hidden md:inline-block text-dgreen2 absolute right-6 mt-0.5 w-4"
                                                v-if="value.id == values[option.id].id"
                                            /> -->
                                            </div>
                                        </div>
                                        <div class="mt-2 space-y-2" v-else-if="option.type == 'products'">
                                            <div
                                                class="rounded-lg hover:bg-gray-50 border cursor-pointer relative items-center"
                                                v-for="value in option.values"
                                                :class="{
                                                    'bg-dgreen2/5 border-dgreen2': data.values[option.id][value.id],
                                                }"
                                                :key="value.id"
                                            >
                                                <label
                                                    :for="value.id"
                                                    class="flex items-center px-4 py-2 cursor-pointer md:text-sm"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        :id="value.id"
                                                        v-model="data.values[option.id][value.id]"
                                                        class="h-5 w-5 border-gray-300 rounded text-dgreen3 transition focus:ring-0 mr-6"
                                                    />
                                                    <vue-picture :image="value.image" :style="'h-8'" class="mr-4" />
                                                    <span class="grow">
                                                        <strong class="inline-block">{{ value.value }}</strong>

                                                        <info-modal
                                                            :title="value.value"
                                                            :body="value.description"
                                                            :infoIcon="true"
                                                            v-if="value.description"
                                                        >
                                                        </info-modal>
                                                        <a
                                                            class="text-dgreen2 ml-4"
                                                            :href="value.url"
                                                            target="_blank"
                                                            :title="'Bekijk ' + value.value"
                                                            >Bekijk product</a
                                                        >
                                                    </span>
                                                    <span v-if="value.price" class="ml-2">{{
                                                        price(value.price)
                                                    }}</span>
                                                </label>

                                                <!-- <fa
                                                :icon="['fal', 'check-circle']"
                                                class="hidden md:inline-block text-dgreen2 absolute right-6 mt-0.5 w-4"
                                                v-if="value.id == values[option.id].id"
                                            /> -->
                                            </div>
                                        </div>
                                        <div class="flex flex-wrap mt-2" v-else-if="option.values.length == 1">
                                            <div v-for="value in option.values" :key="value.id">
                                                <span class="inline-block py-2">
                                                    {{ value.value }}
                                                </span>
                                                <info-modal
                                                    :title="value.value"
                                                    :body="value.description"
                                                    :infoIcon="true"
                                                    v-if="value.description"
                                                >
                                                </info-modal>
                                            </div>
                                        </div>

                                        <div class="flex flex-wrap -mx-2 mt-2" v-else-if="option.values.length < 4">
                                            <div v-for="value in option.values" :key="value.id" class="grow relative">
                                                <div
                                                    v-if="value.is_active"
                                                    class="rounded-md hover:bg-gray-50 border cursor-pointer mx-2 mb-2 relative items-center"
                                                    :class="{
                                                        'bg-dgreen2/5 border-dgreen2':
                                                            value.id == data.values[option.id].id,
                                                    }"
                                                    :key="value.id"
                                                    @click="changeValue(option, value)"
                                                >
                                                    <Link :href="value.url" v-if="value.url" class="inset-0 absolute" />

                                                    <span class="inline-block p-4">
                                                        {{ value.value }}
                                                        {{ price(value.price) }}
                                                    </span>

                                                    <fa
                                                        :icon="['fal', 'check-circle']"
                                                        class="hidden md:inline-block text-dgreen2 absolute right-4 mt-5 w-4"
                                                        v-if="value.id == data.values[option.id].id"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <Listbox
                                            v-else
                                            as="div"
                                            v-model="data.values[option.id]"
                                            class="w-full md:w-1/2 md:pr-6"
                                            @change="changeValue(option, value)"
                                        >
                                            <div class="relative">
                                                <ListboxButton
                                                    class="bg-dgreen2/5 relative w-full border border-dgreen2 rounded-md shadow pl-4 pr-10 py-4 text-left cursor-pointer focus:outline-hidden hover:shadow-md transition-shadow"
                                                >
                                                    <span class="inline-block">{{ data.values[option.id].value }}</span>
                                                    <span
                                                        class="rounded px-2 py-1 text-xs bg-gray-200 ml-2"
                                                        v-if="data.values[option.id].price"
                                                        >+ {{ price(data.values[option.id].price) }}</span
                                                    >
                                                    <span
                                                        class="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none"
                                                    >
                                                        <fa
                                                            :icon="['fal', 'angle-down']"
                                                            class="h-5 w-5 text-gray-400"
                                                        />
                                                    </span>
                                                </ListboxButton>

                                                <!-- <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0"> -->
                                                <transition name="drop">
                                                    <ListboxOptions
                                                        class="absolute z-10 w-full bg-white shadow-lg max-h-60 -mt-16 md:max-h-screen rounded-md py-1 text-base ring-1 ring-black/10 overflow-auto focus:outline-hidden"
                                                    >
                                                        <ListboxOption
                                                            as="template"
                                                            v-for="value in option.values"
                                                            :key="value.id"
                                                            :value="value"
                                                            v-slot="{ active, selected }"
                                                        >
                                                            <div>
                                                                <li
                                                                    :class="[
                                                                        selected || active
                                                                            ? 'bg-gray-100'
                                                                            : 'text-gray-900',
                                                                        'cursor-default select-none relative py-2 pl-4 pr-9',
                                                                    ]"
                                                                >
                                                                    <div
                                                                        :class="[
                                                                            selected ? 'font-semibold' : 'font-normal',
                                                                            'block',
                                                                        ]"
                                                                        class="flex items-center"
                                                                    >
                                                                        {{ value.value }}
                                                                        <span
                                                                            class="text-gray-600 ml-1 inline-block"
                                                                            v-if="value.price"
                                                                        >
                                                                            <span
                                                                                class="rounded px-2 py-1 text-xs bg-gray-200 ml-2"
                                                                                >+ {{ price(value.price) }}</span
                                                                            >
                                                                        </span>
                                                                    </div>

                                                                    <span
                                                                        v-if="selected"
                                                                        class="absolute inset-y-0 right-0 flex items-center pr-4"
                                                                    >
                                                                        <fa
                                                                            :icon="['fal', 'check']"
                                                                            class="h-5 w-5 text-gray-400"
                                                                        />
                                                                    </span>
                                                                </li>
                                                            </div>
                                                        </ListboxOption>
                                                    </ListboxOptions>
                                                </transition>
                                            </div>
                                        </Listbox>
                                    </div>
                                </div>

                                <!-- Totals -->

                                <li
                                    v-if="product && product.discount && product.discount.is_sale"
                                    class="flex flex-wrap my-2"
                                >
                                    <div
                                        class="font-bold bg-black text-white rounded px-3 py-1 w-full mt-2 items-center flex border border-red-700"
                                    >
                                        <fa :icon="['fas', 'fa-star']" class="h-3 w-3 mr-3 text-red-700" />
                                        <div class="grow">
                                            {{ product.discount.title }}
                                        </div>
                                        <span class="text-right">- {{ product.discount.value }}%</span>
                                    </div>
                                </li>

                                <!-- Has discount -->
                                <li v-if="data.totals && data.totals.discount" class="flex flex-wrap">
                                    <div class="w-2/3">Totaal incl. BTW</div>
                                    <div class="w-1/3 text-right">
                                        <s class="block">{{ price(data.totals.total + data.totals.discount.value) }}</s>
                                        <span class="text-2xl font-extrabold">{{ priceEuro(data.totals.total) }}</span>
                                    </div>
                                </li>

                                <!-- Totaal -->
                                <li v-else-if="data.totals" class="flex flex-wrap">
                                    <div class="w-1/2">Totaal incl. BTW</div>
                                    <div class="w-1/2 text-right">
                                        <span class="text-2xl font-extrabold">{{
                                            price(
                                                (data.totals.total -
                                                    (discount && discount.length
                                                        ? discount[0].parsedRawValue * 1.21
                                                        : 0)) *
                                                    (form.quantity ? form.quantity : 1)
                                            )
                                        }}</span>
                                    </div>
                                </li>

                                <div class="flex flex-wrap" v-else>
                                    <div class="w-1/2">Totaal incl. BTW</div>
                                    <div class="w-1/2 text-right">
                                        <strong class="text-2xl font-extrabold">{{
                                            price(data.totals ? data.totals.total : product.price)
                                        }}</strong>
                                    </div>
                                </div>

                                <!-- Prijs -->
                                <div class="w-full border-t my-2 pt-2" v-if="user && (user.is_admin || impersonated)">
                                    <label for="price" class="block text-sm font-semibold mb-2 leading-6 text-gray-900"
                                        >Aangepaste prijs incl. BTW (optioneel)</label
                                    >
                                    <div class="relative mt-2 rounded-md shadow">
                                        <div
                                            class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                                        >
                                            <span class="text-gray-500 sm:text-sm">€</span>
                                        </div>
                                        <input
                                            type="number"
                                            name="price"
                                            id="price"
                                            class="block w-full rounded-md border-0 py-1.5 pl-8 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            placeholder="0,00"
                                            v-model="form.customPrice"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                                        >
                                            <!-- <span class="text-gray-500 sm:text-sm" id="price-currency">EUR</span> -->
                                        </div>
                                    </div>

                                    <!-- Comments for intern usage -->
                                    <div class="mt-3">
                                        <label
                                            for="website"
                                            class="block text-sm font-semibold mb-2 leading-6 text-gray-900"
                                            >Opmerkingen (intern gebruik)</label
                                        >

                                        <textarea
                                            type="text"
                                            name="product_comment"
                                            id="product_comment"
                                            placeholder="Opmerkingen"
                                            v-model="form.comment"
                                            rows="3"
                                            class="text-input relative bg-transparent inline-block"
                                        />
                                    </div>
                                </div>

                                <div class="w-full mb-2 border-t mt-4 pt-4" v-if="user && user.business_account">
                                    <label
                                        for="website"
                                        class="block text-sm font-semibold mb-2 leading-6 text-gray-900"
                                        >Referentie <span class="font-normal text-gray-600">(optioneel)</span></label
                                    >

                                    <input
                                        type="text"
                                        name="reference"
                                        id="reference"
                                        class="block w-full rounded-md border-0 py-1.5 pl-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                        placeholder="Referentie"
                                        v-model="form.reference"
                                    />
                                </div>

                                <!-- Date -->
                                <div class="py-2">
                                    <Date :date="date" :pickup="product.delivery_days == 1 ? firstPickupDate : false" />
                                </div>

                                <!-- Quantity -->
                                <div class="flex flex-wrap">
                                    <div class="w-20 pr-2" v-if="product.quantity_field">
                                        <select class="text-input h-14" v-model="form.quantity">
                                            <option v-for="n in 20" :value="n" :key="n">{{ n }}</option>
                                        </select>
                                    </div>

                                    <div
                                        class="w-auto grow md:grow-0"
                                        :class="product.quantity_field ? 'md:w-2/5' : 'md:w-3/5'"
                                    >
                                        <form @submit.prevent="submit" class="h-full">
                                            <button
                                                type="submit"
                                                :disabled="form.processing"
                                                class="button green w-full"
                                            >
                                                <fa
                                                    :icon="['fad', 'spinner-third']"
                                                    class="text-white opacity-80 mr-2 fa-spin"
                                                    v-if="form.processing"
                                                />
                                                <fa
                                                    v-else
                                                    :icon="['fal', 'shopping-cart']"
                                                    class="text-white mr-2"
                                                />
                                                In winkelwagen
                                            </button>
                                        </form>
                                    </div>

                                    <div class="flex flex-wrap w-full">
                                        <usps class="my-4" />
                                    </div>
                                </div>

                                <a
                                    v-if="product.relativeProduct"
                                    :href="
                                        url('product', product.relativeProduct.slug) +
                                        '?' +
                                        (product.category_id == 16 ? 'hangslot' : 'oplegslot') +
                                        '=1'
                                    "
                                    target="_blank"
                                    class="my-6 flex group relative flex-wrap rounded-lg border border-gray-300 px-5 py-4 hover:border-gray-500 transition-all items-center"
                                >
                                    <div class="w-full font-bold flex justify-between">
                                        <span
                                            >{{ product.category_id == 16 ? 'Hangslot' : 'Oplegslot' }} in combinatie
                                            met cilinderset {{ product.relativeProduct.model }}</span
                                        >

                                        <div class="pr-4 text-right font-medium">
                                            <div>vanaf</div>
                                            <strong class="text-xl">{{
                                                price(
                                                    parseFloat(product.relativeProduct.price) +
                                                        parseFloat(product.price)
                                                )
                                            }}</strong>
                                        </div>
                                    </div>

                                    <div class="w-full flex items-center">
                                        <div class="mx-auto flex items-center md:ml-12">
                                            <img
                                                :src="
                                                    product.category_id == 16
                                                        ? '/img/hangslot-abus.png'
                                                        : '/img/oplegslot.png'
                                                "
                                                class="h-40"
                                            />
                                            <div>
                                                <img
                                                    :src="'/img/set-' + product.model_slug + '.png'"
                                                    class="w-48 h-auto"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <Link
                                        :href="
                                            url('product', product.relativeProduct.slug) +
                                            '?' +
                                            (product.category_id == 16 ? 'hangslot' : 'oplegslot') +
                                            '=1'
                                        "
                                        class="px-4 py-2 absolute right-4 bottom-4 w-fit text-center rounded-md mt-4 border border-gray-400 group-hover:border-gray-600 transition opacity-75 cursor-pointer hidden md:block"
                                        >Set samenstellen <fa :icon="['fal', 'angle-right']" class="w-4 h-4" />
                                    </Link>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="bg-white shadow-invert w-full">
            <div class="max-w-(--breakpoint-2xl) mx-auto px-6 lg:px-8">
                <nav class="py-2 space-x-4 hidden lg:flex" id="product-info-navbar" aria-label="Global">
                    <div class="container flex">
                        <span @click="$scrollTo('#plusminus', 500)" class="product-nav-link"> Product informatie</span>

                        <span
                            @click="$scrollTo('#product-reviews', 500)"
                            class="product-nav-link"
                            v-if="product.reviews.reviews.length"
                        >
                            Reviews</span
                        >

                        <span
                            @click="$scrollTo('#meegeleverd', 500)"
                            class="product-nav-link"
                            v-if="!product.reorder_type && product.type == 'configurable'"
                        >
                            Meegeleverd
                        </span>

                        <span
                            @click="$scrollTo('#knoppen', 500)"
                            class="product-nav-link"
                            v-if="product.reorder_type != 2 && product.type == 'configurable'"
                        >
                            Opties
                        </span>

                        <span
                            @click="$scrollTo('#tekeningen', 500)"
                            class="product-nav-link"
                            v-if="Object.keys(techimages).length"
                        >
                            Technische tekeningen
                        </span>

                  <span
                     @click="$scrollTo('#specs', 500)"
                     class="product-nav-link"
                  >
                     Specificaties
                  </span>
                  <span
                     @click="$scrollTo('#faq', 500)"
                     class="product-nav-link"
                     v-if="product.faq.length"
                  >
                     FAQ
                  </span>
               </div>
            </nav>
         </div>
      </div>
      <div class="bg-white border-t border-gray-100">
         <div class="max-w-(--breakpoint-2xl) container mx-auto px-4 lg:px-4 pt-4 pb-8">
            <div class="flex flex-wrap">
               <div
                  class="w-full md:w-1/2"
                  id="plusminus"
               >
                  <div class="d-prose my-8 md:pr-12">
                     <h2 class="text-xl font-bold mb-4">{{ product.name }}</h2>
                     <Collapse
                        :when="isExpanded"
                        class="v-collapse relative"
                        :baseHeight="220"
                     >
                        <div
                           class="absolute h-32 w-full bottom-0"
                           v-if="!isExpanded"
                        >
                           <div class="w-full h-20 bg-linear-to-t from-white"></div>
                           <div class="w-full h-12 bg-white"></div>
                        </div>
                        <p
                           class="mt-1 mb-4 styled pb-12"
                           v-html="product.description"
                        ></p>
                        <button
                           class="absolute bottom-0 left-1/2 -translate-x-1/2 button default"
                           @click="isExpanded = !isExpanded"
                        >
                           <span v-if="isExpanded">Lees minder</span>
                           <span v-else>Lees meer</span>
                        </button>
                     </Collapse>
                  </div>
                  <div class="w-full mb-8">
                     <strong class="text-xl mb-3 mt-8 inline-block">Plus- en minpunten</strong>

                            <!-- Plus -->
                            <div class="w-full" v-if="product.plus">
                                <ul class="reset-list">
                                    <li v-for="plus in product.plus" :key="plus" class="flex items-center">
                                        <fa :icon="['fas', 'plus-circle']" class="text-green-500 mx-2 shrink" />
                                        <div class="flex">{{ plus }}</div>
                                    </li>
                                </ul>
                            </div>

                            <!-- Minus -->
                            <div class="w-full" v-if="product.minus">
                                <ul class="reset-list">
                                    <li v-for="minus in product.minus" :key="minus" class="flex items-center">
                                        <fa :icon="['fas', 'minus-circle']" class="text-gray-400 mx-2 shrink" />
                                        <div class="flex">{{ minus }}</div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="w-full md:w-1/2">
                        <AdviceWide class="my-6" />
                        <div class="w-full" id="product-reviews" v-if="product.reviews.reviews.length">
                            <product-reviews :slug="product.slug" :reviews="product.reviews" />
                        </div>

                        <div
                            id="tekeningen"
                            class="pb-6 mb-6 border-gray-200 mt-12"
                            v-if="Object.keys(techimages).length"
                        >
                            <div class="w-full flex flex-wrap">
                                <div class="w-1/2 pr-2">
                                    <div class="text-center border p-4 rounded-xl h-full">
                                        <strong class="mb-4 block">Technische tekening</strong>
                                        <vue-picture
                                            :image="techimages.techCylinder"
                                            :name="'Technische tekening'"
                                            :width="650"
                                            :height="650"
                                            fp
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <product-specs :product="product" :options="specifications" class="" />
                <div id="faq">
                    <Faqs :faqs="product.faq" />
                </div>
            </div>
        </div>

        <summary-nav
            :product="product"
            :totals="data.totals"
            :loading="form.processing"
            :image="images[0]"
            v-if="!isSSR && isScrolled"
            class="md:hidden bottom-0 z-20 fixed w-full py-2 bg-white shadow-overallxl"
            @submit="submit"
        />

        <summary-nav
            :product="product"
            :totals="data.totals"
            :loading="form.processing"
            :image="images[0]"
            v-if="!isSSR && isScrolled"
            class="hidden md:block bottom-0 z-20 fixed w-full py-2 bg-white shadow-overallxl"
            @submit="submit"
        />
    </main>
    <div v-if="schemaOrg" v-html="schemaOrg"></div>
    <div v-if="schemaOrgFAQ" v-html="schemaOrgFAQ"></div>
</template>

<script setup>
import Usps from '@/Blocks/Usps.vue'
import ExtraKeys from '@/Components/Configuration/Legacy/ExtraKeys.vue'
import SummaryNav from '@/Components/Configuration/Legacy/SummaryNav.vue'
import ProductReviews from '@/Components/ProductReviews.vue'
import ProductSpecs from '@/Components/ProductSpecs.vue'
import Faqs from '@/Components/Service/Faqs.vue'
import { useCartStore } from '@/stores/cartStore.js'
import { useForm, usePage } from '@inertiajs/vue3'
import axios from 'axios'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { Collapse } from 'vue-collapsed'
import AdviceWide from '../../Blocks/AdviceWide.vue'
const cartStore = useCartStore()
const inBrowser = typeof window !== 'undefined';
// Dynamic import of image component based on environment
import ProductImages from '@/Components/ProductImagesSplide.vue' 

const props = defineProps({
    product: Object,
    images: Object,
    specifications: Object,
    discount: [Object, Boolean],
    date: Object,
    options: Object,
    values: Object,
    logo: String,
    item: [Object, Boolean],
    schemaOrg: [String, Boolean],
    schemaOrgFAQ: [String, Boolean],
    firstPickupDate: Object,
    user: Object,
    showResource: Boolean,
    techimages: Object,
});

const isExpanded = ref(false);
const impersonated = computed(() => usePage().props.impersonated_by);
const data = useForm({
    loading: false,
    values: props.values,
});

const scrollTimeout = ref(null);
const isScrolled = ref(false);

onMounted(() => {
    if (inBrowser) {
        window.addEventListener('scroll', handleScroll);
    }
});

onUnmounted(() => {
    if (inBrowser) {
        window.removeEventListener('scroll', handleScroll);
    }
    if (scrollTimeout.value) {
        clearTimeout(scrollTimeout.value);
    }
});

// Debounced scroll handler function
function handleScroll() {
    if (scrollTimeout.value) {
        clearTimeout(scrollTimeout.value);
    }

    scrollTimeout.value = setTimeout(() => {
        isScrolled.value = window.scrollY > 100;
    }, 100);
}

const isSSR = computed(() => {
    return !inBrowser;
});

const mainImage = computed(() => {
    return props.images[0].src;
});

const mainImageWebp = computed(() => {
    return props.images[0].src;
});

const form = useForm({
    product_id: null,
    customPrice: null,
    comment: null,
    total: 0,
    quantity: 1,
    quantityLocks: 1,
    keys: {
        qty: 0,
        default: 0,
        colors: [],
        customColors: false,
    },
    file: false,
});

if ([16, 84, 101, 102].includes(props.product.category_id)) {
    form.keysSelected = true;
    form.keys = {
        qty: Number(props.product.keys),
        default: props.product.reorder_type == 1 ? 0 : 3,
        colors: props.product.color_caps,
        customColors: [1, 4, 7, 11, 31, 32, 80, 81, 211, 205, 154, 155, 156, 157, 158, 159, 160].includes(
            props.product.id
        )
            ? true
            : false,
    };
}

// Add debouncing utility
const debounce = (fn, wait) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
    };
};

// Optimize calculation with debouncing and request cancellation
let currentCalculateRequest = null;
const debouncedCalculate = debounce(async () => {
    data.loading = true;

    // Cancel previous request if it exists
    if (currentCalculateRequest) {
        currentCalculateRequest.cancel();
    }

    // Create new cancel token
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    currentCalculateRequest = source;

    try {
        const response = await axios.post(
            '/simple/calculate/' + props.product.slug,
            {
                options: data.values,
                keys: form.keys,
                item: props.item ? props.item.id : false,
            },
            { cancelToken: source.token }
        );

        data.totals = response.data;
    } catch (error) {
        if (!axios.isCancel(error)) {
            console.error('Calculation error:', error);
        }
    } finally {
        data.loading = false;
        currentCalculateRequest = null;
    }
}, 300);

// Replace original calculate function
function calculate() {
    debouncedCalculate();
}

// Optimize watchers
watch(
    () => data.values,
    () => {
        calculate();
    },
    { deep: true }
);

watch(() => form.keys.qty, calculate);

// Cache product images
onMounted(() => {
    form.product_id = props.product.id;
    form.total = props.product.price;
    calculate();

    // Preload images if available
    if (props.product.images) {
        props.product.images.forEach((img) => {
            const image = new Image();
            image.src = img.url;
        });
    }

    let gtag = usePage().props.flash.gtag;
    if (gtag) {
        const parsedGtag = JSON.parse(JSON.stringify(gtag));
        dataLayer.push({ ecommerce: null });
        dataLayer.push(parsedGtag);
    }
});

// Cleanup on unmount
onUnmounted(() => {
    if (currentCalculateRequest) {
        currentCalculateRequest.cancel();
    }
});

function submit() {
    form.transform((formData) => ({
        ...formData,
        options: data.values,
        keys: form.keys,
        item: props.item ? props.item.id : false,
    })).post('/winkelwagen', {
        only: ['errors', 'cart', 'gtag', 'flash'],
        onSuccess() {
            cartStore.fetchCartData()
            window.mitt.emit('openCartSlide', true);
        },
    });
}

function changeValue(option, value) {
    data.values[option.id] = value;
    if (value.id != data.values[option.id].id) {
        // calculate();
    }
}
</script>
